import React, { useState, useEffect } from 'react';
import { MainLayout } from '@/components/layout/MainLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { 
  Bell, 
  AlertTriangle, 
  Info,
  CheckCircle,
  XCircle,
  Clock,
  Filter,
  RefreshCw,
  Archive,
  Trash2,
  Eye
} from 'lucide-react';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';
import { api } from '@/services/api';
import { ApiResponse } from '@/types/api.types';

interface SystemAlert {
  alert_id: number;
  type: 'error' | 'warning' | 'info' | 'success';
  category: 'system' | 'security' | 'data' | 'user' | 'backup' | 'eurocat';
  title: string;
  message: string;
  severity: 'critical' | 'high' | 'medium' | 'low';
  status: 'new' | 'acknowledged' | 'resolved' | 'archived';
  created_at: string;
  acknowledged_at: string | null;
  resolved_at: string | null;
  acknowledged_by: string | null;
  resolved_by: string | null;
  affected_component: string | null;
  action_required: boolean;
  auto_resolved: boolean;
}

export const SystemAlerts: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alerts, setAlerts] = useState<SystemAlert[]>([]);
  const [filteredAlerts, setFilteredAlerts] = useState<SystemAlert[]>([]);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterStatus, setFilterStatus] = useState<string>('active');
  const [filterSeverity, setFilterSeverity] = useState<string>('all');
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    loadAlerts();
    // Auto-refresh every 30 seconds
    const interval = setInterval(loadAlerts, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    filterAlerts();
  }, [alerts, filterType, filterStatus, filterSeverity]);

  const loadAlerts = async () => {
    try {
      setError(null);
      const response = await api.get<SystemAlert[]>('/api/admin/alerts');
      
      if (response && response.success && response.data) {
        setAlerts(response.data);
      } else {
        // If API fails, use some sample data for demonstration
        const mockAlerts: SystemAlert[] = [
          {
            alert_id: 1,
            type: 'info',
            category: 'system',
            title: 'Sistem funcțional',
            message: 'Toate componentele sistemului funcționează normal',
            severity: 'low',
            status: 'new',
            created_at: new Date().toISOString(),
            acknowledged_at: null,
            resolved_at: null,
            acknowledged_by: null,
            resolved_by: null,
            affected_component: 'System',
            action_required: false,
            auto_resolved: false
          }
        ];
        setAlerts(mockAlerts);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error loading alerts:', err);
      // Use fallback data if API is not available
      const mockAlerts: SystemAlert[] = [
        {
          alert_id: 1,
          type: 'info',
          category: 'system',
          title: 'API Indisponibil',
          message: 'Sistemul de alerte funcționează în modul offline',
          severity: 'medium',
          status: 'new',
          created_at: new Date().toISOString(),
          acknowledged_at: null,
          resolved_at: null,
          acknowledged_by: null,
          resolved_by: null,
          affected_component: 'API',
          action_required: false,
          auto_resolved: false
        }
      ];
      setAlerts(mockAlerts);
      setLoading(false);
    }
  };

  const filterAlerts = () => {
    let filtered = [...alerts];

    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(a => a.type === filterType);
    }

    // Filter by status
    if (filterStatus === 'active') {
      filtered = filtered.filter(a => a.status === 'new' || a.status === 'acknowledged');
    } else if (filterStatus !== 'all') {
      filtered = filtered.filter(a => a.status === filterStatus);
    }

    // Filter by severity
    if (filterSeverity !== 'all') {
      filtered = filtered.filter(a => a.severity === filterSeverity);
    }

    setFilteredAlerts(filtered);
  };

  const refreshAlerts = async () => {
    setRefreshing(true);
    await loadAlerts();
    setRefreshing(false);
  };

  const acknowledgeAlert = async (alertId: number) => {
    try {
      const response = await api.put(`/api/admin/alerts/${alertId}/acknowledge`);
      if (response && response.success) {
        // Reload alerts to get updated data
        await loadAlerts();
      } else {
        // Fallback to local update
        const updatedAlerts = alerts.map(a => 
          a.alert_id === alertId 
            ? { ...a, status: 'acknowledged' as const, acknowledged_at: new Date().toISOString(), acknowledged_by: 'Current User' }
            : a
        );
        setAlerts(updatedAlerts);
      }
    } catch (err: any) {
      setError('Eroare la confirmarea alertei');
      // Fallback to local update
      const updatedAlerts = alerts.map(a => 
        a.alert_id === alertId 
          ? { ...a, status: 'acknowledged' as const, acknowledged_at: new Date().toISOString(), acknowledged_by: 'Current User' }
          : a
      );
      setAlerts(updatedAlerts);
    }
  };

  const resolveAlert = async (alertId: number) => {
    try {
      const response = await api.put(`/api/admin/alerts/${alertId}/resolve`, {
        resolution: 'Resolved by administrator'
      });
      if (response && response.success) {
        // Reload alerts to get updated data
        await loadAlerts();
      } else {
        // Fallback to local update
        const updatedAlerts = alerts.map(a => 
          a.alert_id === alertId 
            ? { ...a, status: 'resolved' as const, resolved_at: new Date().toISOString(), resolved_by: 'Current User' }
            : a
        );
        setAlerts(updatedAlerts);
      }
    } catch (err: any) {
      setError('Eroare la rezolvarea alertei');
      // Fallback to local update
      const updatedAlerts = alerts.map(a => 
        a.alert_id === alertId 
          ? { ...a, status: 'resolved' as const, resolved_at: new Date().toISOString(), resolved_by: 'Current User' }
          : a
      );
      setAlerts(updatedAlerts);
    }
  };

  const archiveAlert = async (alertId: number) => {
    try {
      const response = await api.put(`/api/admin/alerts/${alertId}/archive`);
      if (response && response.success) {
        // Reload alerts to get updated data
        await loadAlerts();
      } else {
        // Fallback to local update
        const updatedAlerts = alerts.map(a => 
          a.alert_id === alertId 
            ? { ...a, status: 'archived' as const }
            : a
        );
        setAlerts(updatedAlerts);
      }
    } catch (err: any) {
      setError('Eroare la arhivarea alertei');
      // Fallback to local update
      const updatedAlerts = alerts.map(a => 
        a.alert_id === alertId 
          ? { ...a, status: 'archived' as const }
          : a
      );
      setAlerts(updatedAlerts);
    }
  };

  const getAlertIcon = (type: SystemAlert['type']) => {
    switch (type) {
      case 'error':
        return <XCircle className="h-4 w-4" />;
      case 'warning':
        return <AlertTriangle className="h-4 w-4" />;
      case 'info':
        return <Info className="h-4 w-4" />;
      case 'success':
        return <CheckCircle className="h-4 w-4" />;
    }
  };

  const getAlertBadge = (type: SystemAlert['type']) => {
    const config = {
      error: { variant: 'destructive' as const, label: 'Eroare', className: '' },
      warning: { variant: 'secondary' as const, label: 'Avertisment', className: '' },
      info: { variant: 'default' as const, label: 'Info', className: '' },
      success: { variant: 'default' as const, label: 'Succes', className: 'bg-green-500' }
    };
    const { variant, label, className } = config[type];
    return <Badge variant={variant} className={className}>{label}</Badge>;
  };

  const getSeverityBadge = (severity: SystemAlert['severity']) => {
    const config = {
      critical: { variant: 'destructive' as const, label: 'Critic', className: '' },
      high: { variant: 'destructive' as const, label: 'Înalt', className: 'bg-orange-500' },
      medium: { variant: 'secondary' as const, label: 'Mediu', className: '' },
      low: { variant: 'outline' as const, label: 'Scăzut', className: '' }
    };
    const { variant, label, className } = config[severity];
    return <Badge variant={variant} className={className || ''}>{label}</Badge>;
  };

  const getStatusBadge = (status: SystemAlert['status']) => {
    const config = {
      new: { variant: 'destructive' as const, label: 'Nou', className: '' },
      acknowledged: { variant: 'secondary' as const, label: 'Confirmat', className: '' },
      resolved: { variant: 'default' as const, label: 'Rezolvat', className: 'bg-green-500' },
      archived: { variant: 'outline' as const, label: 'Arhivat', className: '' }
    };
    const { variant, label, className } = config[status];
    return <Badge variant={variant} className={className || ''}>{label}</Badge>;
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-96 w-full" />
        </div>
      </MainLayout>
    );
  }

  const activeAlerts = alerts.filter(a => a.status === 'new' || a.status === 'acknowledged');
  const criticalAlerts = activeAlerts.filter(a => a.severity === 'critical');
  const highAlerts = activeAlerts.filter(a => a.severity === 'high');

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Alerte sistem</h1>
            <p className="text-muted-foreground">
              Monitorizează și gestionează alertele sistemului
            </p>
          </div>
          <Button onClick={refreshAlerts} disabled={refreshing}>
            <RefreshCw className={`mr-2 h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
            Reîmprospătare
          </Button>
        </div>

        {/* Alert Summary */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Alerte active</CardTitle>
              <Bell className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{activeAlerts.length}</div>
              <p className="text-xs text-muted-foreground">
                {criticalAlerts.length} critice, {highAlerts.length} prioritate înaltă
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Critice</CardTitle>
              <XCircle className="h-4 w-4 text-destructive" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-destructive">{criticalAlerts.length}</div>
              <p className="text-xs text-muted-foreground">Necesită atenție imediată</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Rezolvate azi</CardTitle>
              <CheckCircle className="h-4 w-4 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {alerts.filter(a => 
                  a.status === 'resolved' && 
                  a.resolved_at && 
                  new Date(a.resolved_at).toDateString() === new Date().toDateString()
                ).length}
              </div>
              <p className="text-xs text-muted-foreground">În ultimele 24 ore</p>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">Acțiune necesară</CardTitle>
              <AlertTriangle className="h-4 w-4 text-orange-500" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {activeAlerts.filter(a => a.action_required).length}
              </div>
              <p className="text-xs text-muted-foreground">Necesită intervenție manuală</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center">
              <Filter className="mr-2 h-5 w-5" />
              Filtre
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4">
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Tip alertă" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate tipurile</SelectItem>
                  <SelectItem value="error">Erori</SelectItem>
                  <SelectItem value="warning">Avertismente</SelectItem>
                  <SelectItem value="info">Informații</SelectItem>
                  <SelectItem value="success">Succes</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="new">Noi</SelectItem>
                  <SelectItem value="acknowledged">Confirmate</SelectItem>
                  <SelectItem value="resolved">Rezolvate</SelectItem>
                  <SelectItem value="archived">Arhivate</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterSeverity} onValueChange={setFilterSeverity}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Severitate" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toate</SelectItem>
                  <SelectItem value="critical">Critic</SelectItem>
                  <SelectItem value="high">Înalt</SelectItem>
                  <SelectItem value="medium">Mediu</SelectItem>
                  <SelectItem value="low">Scăzut</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Error Alert */}
        {error && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {/* Alerts Table */}
        <Card>
          <CardHeader>
            <CardTitle>Listă alerte ({filteredAlerts.length})</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tip</TableHead>
                  <TableHead>Titlu</TableHead>
                  <TableHead>Categorie</TableHead>
                  <TableHead>Severitate</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Creat</TableHead>
                  <TableHead>Componentă</TableHead>
                  <TableHead className="text-right">Acțiuni</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredAlerts.map((alert) => (
                  <TableRow key={alert.alert_id}>
                    <TableCell>{getAlertBadge(alert.type)}</TableCell>
                    <TableCell>
                      <div>
                        <div className="font-medium">{alert.title}</div>
                        <div className="text-sm text-muted-foreground">{alert.message}</div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">{alert.category}</Badge>
                    </TableCell>
                    <TableCell>{getSeverityBadge(alert.severity)}</TableCell>
                    <TableCell>{getStatusBadge(alert.status)}</TableCell>
                    <TableCell>
                      <div className="text-sm">
                        {format(new Date(alert.created_at), 'dd MMM HH:mm', { locale: ro })}
                      </div>
                    </TableCell>
                    <TableCell>
                      <span className="text-sm">{alert.affected_component}</span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        {alert.status === 'new' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => acknowledgeAlert(alert.alert_id)}
                            title="Confirmă"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        )}
                        {(alert.status === 'new' || alert.status === 'acknowledged') && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => resolveAlert(alert.alert_id)}
                            title="Rezolvă"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                        {alert.status === 'resolved' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => archiveAlert(alert.alert_id)}
                            title="Arhivează"
                          >
                            <Archive className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredAlerts.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Nu există alerte care să corespundă filtrelor selectate
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </MainLayout>
  );
};